import {
  ViewerAdapterError,
  normalizeLayoutPolygon,
  type AreaTag,
  type LayoutHotspotSummary,
  type LayoutMarker,
  type PanoramaHotspot,
  type PanoramaPhoto,
  type PdfRotation,
  type ViewerLayout,
  type ViewerPhoto,
} from '@ironview/interactive-viewer';

import type { LayoutPdfInfo } from '../api/layout-pdf-api';
import type { AreaTag as Ironview360AreaTag } from '@/features/area-tags/types/area-tags';
import type {
  Layout,
  LayoutHotspotListItem,
  Photo,
  Photo360Response,
  PhotoHotspot,
  PhotoMarker,
  Unit,
} from '../types/types';

export interface Ironview360AreaTagSource {
  id: number;
  name?: string;
  points: readonly (readonly number[] | { x: number; y: number })[];
  layout?: string | null;
  layout_id?: string;
  images?: Readonly<
    Record<string, readonly { id: number; name: string; date?: string; scan_type?: string }[]>
  >;
  marker_ids?: readonly number[];
  text_position?: readonly [number, number] | null;
  text_size?: number | null;
  text_rotation?: number | null;
}

export interface ViewerLayoutNormalizationOptions {
  pdfInfo?: LayoutPdfInfo | null;
  imageDimensions?: {
    width: number;
    height: number;
  } | null;
}

const finitePositive = (value: number | undefined): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : undefined;

const uniqueNumbers = (values: readonly number[]): number[] =>
  [...new Set(values.filter((value) => Number.isFinite(value)))];

const normalizeAreaTagPoints = (
  points: Ironview360AreaTagSource['points'],
): readonly (readonly number[])[] =>
  points.map((point) =>
    Array.isArray(point)
      ? point
      : [(point as { x: number; y: number }).x, (point as { x: number; y: number }).y],
  );

export const normalizePdfRotation = (rotation: number | null | undefined): PdfRotation => {
  if (!Number.isFinite(rotation)) return 0;
  const normalized = ((Number(rotation) % 360) + 360) % 360;
  return normalized === 90 || normalized === 180 || normalized === 270
    ? normalized
    : 0;
};

export const toViewerPhoto = (photo: Photo): ViewerPhoto => ({
  id: photo.id,
  name: photo.name,
  date: photo.date || undefined,
  scanType: photo.scan_type || undefined,
});

const optionalNumericId = (
  value: string | number | undefined,
): number | undefined => {
  if (value == null || value === '') return undefined;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : undefined;
};

export const fromViewerPhoto = (photo: ViewerPhoto): Photo => ({
  id: Number(photo.id),
  name: photo.name,
  date: photo.date ?? '',
  scan_type: photo.scanType ?? '',
});

export const toViewerLayoutMarker = (marker: PhotoMarker): LayoutMarker => ({
  id: marker.id,
  persistenceId: marker.dbId,
  index: marker.number,
  x: marker.x,
  y: marker.y,
  width: marker.width,
  height: marker.height,
  fontSize: marker.fontSize,
  photos: marker.photos.map(toViewerPhoto),
});

export const fromViewerLayoutMarker = (marker: LayoutMarker): PhotoMarker => ({
  id: optionalNumericId(marker.id) ?? marker.index,
  dbId: optionalNumericId(marker.persistenceId),
  number: marker.index,
  x: marker.x,
  y: marker.y,
  width: marker.width,
  height: marker.height,
  fontSize: marker.fontSize,
  photos: marker.photos.map(fromViewerPhoto),
});

export const toViewerAreaTag = (
  source: Ironview360AreaTagSource | Unit,
): AreaTag => {
  const imageGroups = source.images ?? {};
  const fallbackName = Object.keys(imageGroups)[0] || `Area ${source.id}`;
  const sourceName = 'name' in source ? source.name : undefined;
  const sourceLayoutId = 'layout_id' in source ? source.layout_id : source.layout;
  const photoIds = Object.values(imageGroups).flatMap((photos) =>
    photos.map((photo) => photo.id),
  );

  return {
    id: source.id,
    name: sourceName?.trim() || fallbackName,
    points: normalizeLayoutPolygon(normalizeAreaTagPoints(source.points)),
    markerIds: uniqueNumbers(source.marker_ids ?? []),
    photoIds: uniqueNumbers(photoIds),
    label: {
      position: source.text_position
        ? [source.text_position[0], source.text_position[1]]
        : null,
      size: source.text_size ?? null,
      rotation: source.text_rotation ?? null,
    },
    metadata: {
      sourceLayoutId: sourceLayoutId ?? null,
      source,
    },
  };
};

export const fromViewerAreaTag = (areaTag: AreaTag): Unit => {
  const source =
    areaTag.metadata?.source &&
    typeof areaTag.metadata.source === 'object'
      ? (areaTag.metadata.source as Partial<Unit>)
      : {};
  const sourceImages =
    source.images && typeof source.images === 'object'
      ? source.images
      : undefined;
  const images: Unit['images'] =
    sourceImages
      ? Object.fromEntries(
          Object.entries(sourceImages).map(([name, photos]) => [
            name,
            photos.map((photo) => ({
              id: Number(photo.id),
              name: photo.name,
              date: photo.date ?? '',
              scan_type: photo.scan_type ?? '',
            })),
          ]),
        )
      : {
          [areaTag.name]: areaTag.photoIds.map((photoId) => ({
            id: Number(photoId),
            name: `Photo ${photoId}`,
            date: '',
            scan_type: '',
          })),
        };

  return {
    id: Number(areaTag.id),
    points: areaTag.points.map(([x, y]) => [x, y]),
    layout:
      typeof source.layout === 'string'
        ? source.layout
        : typeof areaTag.metadata?.sourceLayoutId === 'string'
          ? areaTag.metadata.sourceLayoutId
          : undefined,
    images,
    marker_ids: areaTag.markerIds.map(Number),
    text_position: areaTag.label?.position
      ? [areaTag.label.position[0], areaTag.label.position[1]]
      : null,
    text_size: areaTag.label?.size ?? null,
    text_rotation: areaTag.label?.rotation ?? null,
  };
};

export const fromViewerAreaTagRecord = (
  areaTag: AreaTag,
): Ironview360AreaTag => {
  const source =
    areaTag.metadata?.source &&
    typeof areaTag.metadata.source === 'object'
      ? (areaTag.metadata.source as Partial<Ironview360AreaTag>)
      : {};
  const sourceLayoutId = areaTag.metadata?.sourceLayoutId;
  const layoutId =
    source.layout_id ??
    (typeof sourceLayoutId === 'string' ||
    typeof sourceLayoutId === 'number'
      ? String(sourceLayoutId)
      : '');

  return {
    id: Number(areaTag.id),
    name: areaTag.name,
    points: areaTag.points.map(([x, y]) => ({ x, y })),
    layout: source.layout ?? null,
    layout_id: layoutId,
    images:
      source.images ??
      {
        [areaTag.name]: areaTag.photoIds.map((photoId) => ({
          id: Number(photoId),
          name: `Photo ${photoId}`,
        })),
      },
    marker_ids: areaTag.markerIds.map(Number),
    text_position: areaTag.label?.position
      ? [areaTag.label.position[0], areaTag.label.position[1]]
      : null,
    text_size: areaTag.label?.size ?? null,
    text_rotation: areaTag.label?.rotation ?? null,
    created_at: source.created_at,
    updated_at: source.updated_at,
  };
};

export const toViewerLayout = (
  layout: Layout,
  options: ViewerLayoutNormalizationOptions = {},
): ViewerLayout => {
  const imageUrl =
    layout.image_url || layout.signed_photo_url || layout.photo_url || undefined;
  const pdfInfo = options.pdfInfo;
  const pdfUrl = pdfInfo?.pdf_file_url || layout.pdf_file_url || undefined;
  const hasPdfReference = Boolean(
    layout.has_pdf ||
      layout.pdf_file_id ||
      layout.pdf_page_id ||
      layout.pdf_file_url ||
      pdfInfo,
  );

  if (imageUrl && hasPdfReference) {
    throw new ViewerAdapterError(
      `Layout ${layout.id} cannot contain both an image and a PDF asset`,
      { code: 'conflict' },
    );
  }

  if (hasPdfReference) {
    if (!pdfUrl) {
      throw new ViewerAdapterError(
        `Layout ${layout.id} has a PDF reference but no readable PDF URL`,
        { code: 'expired-asset-url', retryable: true },
      );
    }

    const cropRegion = layout.pdf_crop_region ?? null;
    const width = cropRegion?.[2] ?? pdfInfo?.width;
    const height = cropRegion?.[3] ?? pdfInfo?.height;

    return {
      id: layout.id,
      name: layout.name,
      type: layout.type,
      asset: {
        kind: 'pdf',
        assetId:
          pdfInfo?.pdf_file_id ?? layout.pdf_file_id ?? layout.pdf_page_id,
        url: pdfUrl,
        pageNumber: pdfInfo?.page_number ?? layout.pdf_page_number ?? 1,
        pageCount: finitePositive(pdfInfo?.page_count),
        width,
        height,
        rotation:
          pdfInfo?.rotation == null
            ? undefined
            : normalizePdfRotation(pdfInfo.rotation),
        dpi: pdfInfo?.dpi,
        cropRegion,
        flippedHorizontal: Boolean(layout.flipped_horizontal),
        flippedVertical: Boolean(layout.flipped_vertical),
      },
      metadata: {
        pdfPageId: pdfInfo?.pdf_page_id ?? layout.pdf_page_id,
        betaFeaturesEnabled: Boolean(layout.beta_features_enabled),
      },
    };
  }

  if (imageUrl) {
    return {
      id: layout.id,
      name: layout.name,
      type: layout.type,
      asset: {
        kind: 'image',
        url: imageUrl,
        width: finitePositive(options.imageDimensions?.width),
        height: finitePositive(options.imageDimensions?.height),
        flippedHorizontal: Boolean(layout.flipped_horizontal),
        flippedVertical: Boolean(layout.flipped_vertical),
      },
      metadata: {
        betaFeaturesEnabled: Boolean(layout.beta_features_enabled),
      },
    };
  }

  return {
    id: layout.id,
    name: layout.name,
    type: layout.type,
    asset: { kind: 'empty' },
    metadata: {
      betaFeaturesEnabled: Boolean(layout.beta_features_enabled),
    },
  };
};

export const fromViewerLayout = (layout: ViewerLayout): Layout => {
  const betaFeaturesEnabled = Boolean(
    layout.metadata?.betaFeaturesEnabled,
  );
  const baseLayout: Layout = {
    id: String(layout.id),
    name: layout.name,
    type: layout.type,
    beta_features_enabled: betaFeaturesEnabled,
  };

  if (layout.asset.kind === 'image') {
    return {
      ...baseLayout,
      image_url: layout.asset.url,
      flipped_horizontal: layout.asset.flippedHorizontal,
      flipped_vertical: layout.asset.flippedVertical,
    };
  }

  if (layout.asset.kind === 'pdf') {
    return {
      ...baseLayout,
      has_pdf: true,
      pdf_file_id:
        layout.asset.assetId == null
          ? undefined
          : String(layout.asset.assetId),
      pdf_file_url: layout.asset.url,
      pdf_page_id:
        layout.metadata?.pdfPageId == null
          ? undefined
          : String(layout.metadata.pdfPageId),
      pdf_page_number: layout.asset.pageNumber,
      pdf_crop_region: layout.asset.cropRegion
        ? [...layout.asset.cropRegion]
        : null,
      flipped_horizontal: layout.asset.flippedHorizontal,
      flipped_vertical: layout.asset.flippedVertical,
    };
  }

  return baseLayout;
};

export const toViewerPanoramaPhoto = (
  photo: Photo360Response,
): PanoramaPhoto => {
  const imageUrl = photo.image_url || photo.gcs_url || photo.photo_link;
  if (!imageUrl) {
    throw new ViewerAdapterError(
      `Photo ${photo.photo_id} does not have a readable panorama URL`,
      { code: 'expired-asset-url', retryable: true },
    );
  }

  return {
    id: photo.photo_id,
    name: photo.photo_name,
    date: photo.date || undefined,
    imageUrl,
    previousPhotoId: photo.prev_photo,
    nextPhotoId: photo.next_photo,
    previousDatePhotoId: photo.prev_date_photo_id,
    nextDatePhotoId: photo.next_date_photo_id,
    metadata: {
      layoutId: photo.layout_id ?? null,
      layoutName: photo.layout_name ?? null,
      layoutType: photo.layout_type ?? null,
    },
  };
};

export const fromViewerPanoramaPhoto = (
  photo: PanoramaPhoto,
): Photo360Response => ({
  photo_id: Number(photo.id),
  photo_name: photo.name,
  date: photo.date ?? '',
  image_url: photo.imageUrl,
  layout_id:
    photo.metadata?.layoutId == null
      ? null
      : Number(photo.metadata.layoutId),
  layout_name:
    typeof photo.metadata?.layoutName === 'string'
      ? photo.metadata.layoutName
      : null,
  layout_type:
    photo.metadata?.layoutType === 'Progress' ||
    photo.metadata?.layoutType === 'Asbuild' ||
    photo.metadata?.layoutType === 'Corridor'
      ? photo.metadata.layoutType
      : null,
  prev_photo:
    photo.previousPhotoId == null
      ? photo.previousPhotoId
      : Number(photo.previousPhotoId),
  next_photo:
    photo.nextPhotoId == null
      ? photo.nextPhotoId
      : Number(photo.nextPhotoId),
  prev_date_photo_id:
    photo.previousDatePhotoId == null
      ? photo.previousDatePhotoId
      : Number(photo.previousDatePhotoId),
  next_date_photo_id:
    photo.nextDatePhotoId == null
      ? photo.nextDatePhotoId
      : Number(photo.nextDatePhotoId),
});

export type Ironview360PhotoHotspotSource = Pick<
  PhotoHotspot,
  'id' | 'pitch' | 'yaw'
> &
  Partial<Omit<PhotoHotspot, 'id' | 'pitch' | 'yaw'>>;

export const toViewerPanoramaHotspot = (
  hotspot: Ironview360PhotoHotspotSource,
  fallbackPhotoId?: string | number,
): PanoramaHotspot => ({
  id: hotspot.id,
  photoId: hotspot.photo_id ?? fallbackPhotoId ?? 0,
  pitch: hotspot.pitch,
  yaw: hotspot.yaw,
  hfov: hotspot.hfov ?? 100,
  title: hotspot.title ?? null,
  objects: (hotspot.objects ?? []).map((object) => ({
    id: object.id,
    type: object.type,
  })),
  createdAt: hotspot.created_at,
  updatedAt: hotspot.updated_at ?? hotspot.created_at,
  metadata: {
    overlayCount: hotspot.overlay_count ?? 0,
    dominantTrade: hotspot.dominant_trade ?? null,
    source: hotspot,
  },
});

export const toViewerPanoramaHotspots = (
  hotspots: readonly Ironview360PhotoHotspotSource[],
  fallbackPhotoId?: string | number,
): PanoramaHotspot[] =>
  hotspots.map((hotspot) =>
    toViewerPanoramaHotspot(hotspot, fallbackPhotoId),
  );

export const fromViewerPanoramaHotspot = (
  hotspot: PanoramaHotspot,
): PhotoHotspot => {
  const source =
    hotspot.metadata?.source &&
    typeof hotspot.metadata.source === 'object'
      ? (hotspot.metadata.source as Partial<PhotoHotspot>)
      : {};

  return {
    ...source,
    id: Number(hotspot.id),
    photo_id: Number(hotspot.photoId),
    pitch: hotspot.pitch,
    yaw: hotspot.yaw,
    hfov: hotspot.hfov,
    title: hotspot.title ?? source.title,
    objects:
      source.objects ??
      hotspot.objects.map((object) => ({
        id: object.id,
        type: object.type as 'note' | 'image' | 'label',
        title: '',
        created_at: '',
        updated_at: '',
      })),
    overlay_count: Number(
      hotspot.metadata?.overlayCount ?? source.overlay_count ?? 0,
    ),
    created_by: source.created_by ?? null,
    created_at: hotspot.createdAt ?? source.created_at ?? '',
    updated_at: hotspot.updatedAt ?? source.updated_at ?? '',
    dominant_trade:
      (hotspot.metadata?.dominantTrade as PhotoHotspot['dominant_trade']) ??
      source.dominant_trade ??
      null,
  };
};

export const toViewerLayoutHotspotSummary = (
  hotspot: LayoutHotspotListItem,
  layoutId: string | number,
): LayoutHotspotSummary => ({
  layoutId,
  markerIndex: hotspot.photo_index,
  hotspot: {
    id: hotspot.id,
    photoId: hotspot.photo_id,
    pitch: hotspot.pitch,
    yaw: hotspot.yaw,
    hfov: hotspot.hfov,
    title: hotspot.title,
    objects: [],
    createdAt: hotspot.created_at,
    updatedAt: hotspot.updated_at,
    metadata: {
      overlayCount: hotspot.overlay_count,
      openIssueCounts: hotspot.open_issue_counts,
      createdByDisplay: hotspot.created_by_display,
      layoutName: hotspot.layout_name,
    },
  },
});

export const fromViewerLayoutHotspotSummary = (
  summary: LayoutHotspotSummary,
): LayoutHotspotListItem => {
  const { hotspot } = summary;
  const issueCounts = hotspot.metadata?.openIssueCounts;
  const normalizedIssueCounts =
    issueCounts && typeof issueCounts === 'object'
      ? (issueCounts as LayoutHotspotListItem['open_issue_counts'])
      : {
          observations: 0,
          punch_items: 0,
          rfis: 0,
          submittals: 0,
          safety: 0,
          total: 0,
        };

  return {
    id: Number(hotspot.id),
    photo_id: Number(hotspot.photoId),
    title: hotspot.title ?? null,
    pitch: hotspot.pitch,
    yaw: hotspot.yaw,
    hfov: hotspot.hfov,
    created_at: hotspot.createdAt ?? '',
    updated_at: hotspot.updatedAt ?? '',
    overlay_count: Number(hotspot.metadata?.overlayCount ?? 0),
    open_issue_counts: normalizedIssueCounts,
    created_by_display:
      typeof hotspot.metadata?.createdByDisplay === 'string'
        ? hotspot.metadata.createdByDisplay
        : null,
    layout_name:
      typeof hotspot.metadata?.layoutName === 'string'
        ? hotspot.metadata.layoutName
        : null,
    photo_index: summary.markerIndex ?? null,
  };
};
